import { React, useState, useEffect, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import RDataGrid from "../reusables/RDataGrid";
import PageLoader from "../loader/PageLoader";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import AddPopup from "../popup/AddPopup";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import AddLocation from "../forms/AddLocation";
import { LightTooltip } from "../tooltip/ToolTip";

const Locations = () => {
  const [isFetchingLocations, setIsFetchingLocations] = useState(true);
  const [locations, setLocations] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshLocations, setSetRefreshLocations] = useState(null);

  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onRemoveLocation = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      // Request payload
      const data = {
        locationId: id,
        tenantId: auth.tenantId,
      };

      const TENANT_URL = "/api/v1/tenants/remove-branch";
      const response = await axiosPrivate.post(TENANT_URL, data, {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshLocations(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Branch",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onRemoveLocation(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingLocations) {
      setIsFetchingLocations(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getBranches = async () => {
      try {
        const USER_URL = "api/v1/users/";
        const response = await axiosPrivate.get(
          `${USER_URL}${auth.id}`,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        //const cUser = JSON.stringify(response.data.data, null, 4);

        isMounted && setLocations(response.data.data.managerTenant.locations);
        setIsFetchingLocations(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingLocations(false);
      }
    };

    getBranches();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshLocations]);

  const columns = useMemo(
    () => [
      {
        field: "rowNo", // from DataGrid rows defifnition
        headerName: "No.",
        sortable: false,
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "name",
        headerName: "Name",
        headerClassName: "super-app-theme--header",
        minWidth: 160,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerClassName: "super-app-theme--header",
        align: "center",
        headerAlign: "center",
        minWidth: 120,
        flex: 1,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Delete branch">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this branch?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onRemoveLocation(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful branch add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshLocations(new Date());
  };

  return isFetchingLocations ? (
    <PageLoader title="Loading branches..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {locations?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              sx={{
                textAlign: "left",
                mt: 3,
                mb: 3,
                fontSize: {
                  lg: 30,
                  md: 20,
                  sm: 20,
                  xs: 19,
                },
              }}
            >
              Branches
            </Typography>

            <Button
              sx={{
                size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
              }}
              variant="solid"
              color="primary"
              onClick={function () {
                //setEditSupplier(false);
                //setSelectedSupplier(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Branch
            </Button>
          </div>
          <Box
            sx={{
              width: "100%",
              margin: "0 auto 16px",
              height: 560,
              overflow: "hidden",
            }}
          >
            <RDataGrid columns={columns} rows={locations} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={"Add Branch"}
          >
            <AddLocation
              closeDialog={closeDialog}
              //editSupplier={editSupplier}
              //selectedSupplier={selectedSupplier}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No branches to display
          </Typography>
          <Button
            sx={{
              size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
            }}
            variant="solid"
            color="primary"
            onClick={function () {
              //setEditSupplier(false);
              //setSelectedSupplier(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Branch
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={"Add Branch"}
          >
            <AddLocation
              closeDialog={closeDialog}
              //editSupplier={editSupplier}
              //selectedSupplier={selectedSupplier}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Locations;
